import { useEffect, useRef, useState } from "react";
import type { IndicatorCatalogEntry } from "../api";

export interface ActiveIndicatorSpec {
  name: string;
  params: Record<string, number>;
}

export function indicatorKey(spec: ActiveIndicatorSpec): string {
  return `${spec.name}:${JSON.stringify(spec.params)}`;
}

/** The "+ Indicators" picker (ui_spec A7: "adds/removes any indicator at any
 * parameter; active indicators show as removable chips"). `catalog` is
 * fetched live from GET /instrument/indicators/catalog (59 TA-Lib
 * indicators across 5 groups) -- not a hardcoded list, so a catalog change
 * shows up here with no frontend edit, same discipline as the Stocks
 * page's filterable-fields picker.
 *
 * SCOPE: only indicators whose default_params includes a single
 * `timeperiod`-shaped key get an inline period editor in the active chip
 * (the common case -- SMA/EMA/RSI/ATR/...). Multi-parameter indicators
 * (MACD's 3 periods, BBANDS' std-dev multipliers, STOCH's 5 params) are
 * addable/removable with their TA-Lib defaults but not yet
 * per-parameter-editable from this UI -- a real, stated limit, not silently
 * dropped functionality. */
export function editablePeriodKey(entry: IndicatorCatalogEntry): string | null {
  const keys = Object.keys(entry.default_params);
  const candidate = keys.find((k) => k === "timeperiod");
  return candidate && keys.length === 1 ? candidate : null;
}

export function IndicatorPicker({
  catalog,
  active,
  onAdd,
  onRemove,
}: {
  catalog: IndicatorCatalogEntry[];
  active: ActiveIndicatorSpec[];
  onAdd: (spec: ActiveIndicatorSpec) => void;
  onRemove: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  function isActive(entry: IndicatorCatalogEntry): boolean {
    return active.some((a) => a.name === entry.name);
  }

  function toggle(entry: IndicatorCatalogEntry) {
    const existing = active.find((a) => a.name === entry.name);
    if (existing) {
      onRemove(indicatorKey(existing));
    } else {
      onAdd({ name: entry.name, params: entry.default_params });
    }
  }

  const filtered = search.trim()
    ? catalog.filter(
        (c) =>
          c.display_name.toLowerCase().includes(search.toLowerCase()) ||
          c.name.toLowerCase().includes(search.toLowerCase()),
      )
    : catalog;
  const overlays = filtered.filter((c) => c.overlay);
  const subPanels = filtered.filter((c) => !c.overlay);

  return (
    <div className="indicator-picker" ref={ref}>
      <button className="toggle" onClick={() => setOpen((o) => !o)}>
        <span style={{ color: "var(--accent)" }}>+ Indicators</span>
      </button>
      {open && (
        <div className="columns-popover ind-menu">
          <div className="cp-search">
            <span>⌕</span>
            <input placeholder="Find an indicator…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>
          <div className="cp-scroll">
            <div className="cp-group-label">Overlays · on price ({overlays.length})</div>
            {overlays.map((c) => (
              <label className="col-row" key={c.name} title={c.display_name}>
                <input type="checkbox" checked={isActive(c)} onChange={() => toggle(c)} />
                {c.display_name}
              </label>
            ))}
            <div className="cp-group-label">Sub-panel · own scale ({subPanels.length})</div>
            {subPanels.map((c) => (
              <label className="col-row" key={c.name} title={c.display_name}>
                <input type="checkbox" checked={isActive(c)} onChange={() => toggle(c)} />
                {c.display_name}
              </label>
            ))}
            {filtered.length === 0 && <div className="cp-note">No indicator matches "{search}".</div>}
          </div>
          <div className="cp-note">
            59 TA-Lib indicators (Overlap/Momentum/Volatility/Volume/Cycle). Pattern-recognition and generic math
            functions aren't chart line indicators, so they're not offered here.
          </div>
        </div>
      )}
      {active.length > 0 && (
        <div className="ind-chips">
          {active.map((spec) => {
            const entry = catalog.find((c) => c.name === spec.name);
            const key = indicatorKey(spec);
            return (
              <span className="indchip" key={key}>
                {entry?.display_name ?? spec.name}
                {Object.entries(spec.params).length > 0 && entry && editablePeriodKey(entry) && (
                  <PeriodEditor
                    value={spec.params[editablePeriodKey(entry)!]}
                    onChange={(v) => {
                      onRemove(key);
                      onAdd({ name: spec.name, params: { [editablePeriodKey(entry)!]: v } });
                    }}
                  />
                )}
                <span className="x" onClick={() => onRemove(key)}>
                  ×
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PeriodEditor({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [local, setLocal] = useState(String(value));
  return (
    <input
      className="period-editor"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        const n = Number(local);
        if (Number.isFinite(n) && n > 0 && n !== value) onChange(n);
        else setLocal(String(value));
      }}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
    />
  );
}
